import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Paper,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

/**
 * KeywordBuilder
 *
 * Visual builder for keyword logic:
 *   - Groups are joined by AND (comma in serialized form)
 *   - Keywords within a group are joined by OR (plus in serialized form)
 *
 * Props:
 *   value     - string in "kw1+kw2,kw3" format (current serialized value)
 *   onChange  - callback(newSerializedString)
 *   error     - optional error string
 *   disabled  - optional boolean
 */
const KeywordBuilder = ({
  value = "",
  onChange,
  error,
  disabled = false,
  hideAddRequiredGroup = false,
  title = "Keywords to Check",
  suggestAction = null,
}) => {
  // groups: Array<string[]>  — each group is an array of OR-alternatives
  const [groups, setGroups] = useState([]);
  // per-group input buffers
  const [inputs, setInputs] = useState([]);

  // Parse serialized value → groups on mount / external change
  useEffect(() => {
    if (!value || value.trim() === "") {
      setGroups([[]]);
      setInputs([""]);
      return;
    }
    const parsed = value.split(",").map((g) =>
      g
        .split("+")
        .map((k) => k.trim())
        .filter(Boolean),
    );
    setGroups(parsed.length > 0 ? parsed : [[]]);
    setInputs(parsed.map(() => ""));
  }, [value]);

  // Serialize groups → string and fire onChange
  const serialize = useCallback(
    (newGroups) => {
      const str = newGroups
        .filter((g) => g.length > 0)
        .map((g) => g.join("+"))
        .join(",");
      if (onChange) onChange(str);
    },
    [onChange],
  );

  const addKeywordToGroup = (groupIdx) => {
    const kw = inputs[groupIdx]?.trim();
    if (!kw) return;
    const newGroups = groups.map((g, i) => (i === groupIdx ? [...g, kw] : g));
    const newInputs = inputs.map((v, i) => (i === groupIdx ? "" : v));
    setGroups(newGroups);
    setInputs(newInputs);
    serialize(newGroups);
  };

  const removeKeyword = (groupIdx, kwIdx) => {
    const newGroups = groups.map((g, i) =>
      i === groupIdx ? g.filter((_, j) => j !== kwIdx) : g,
    );
    setGroups(newGroups);
    serialize(newGroups);
  };

  const removeGroup = (groupIdx) => {
    const newGroups = groups.filter((_, i) => i !== groupIdx);
    const newInputs = inputs.filter((_, i) => i !== groupIdx);
    const finalGroups = newGroups.length > 0 ? newGroups : [[]];
    const finalInputs = newInputs.length > 0 ? newInputs : [""];
    setGroups(finalGroups);
    setInputs(finalInputs);
    serialize(finalGroups);
  };

  const addGroup = () => {
    setGroups([...groups, []]);
    setInputs([...inputs, ""]);
  };

  const handleInputChange = (groupIdx, val) => {
    setInputs(inputs.map((v, i) => (i === groupIdx ? val : v)));
  };

  const handleInputKeyDown = (e, groupIdx) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addKeywordToGroup(groupIdx);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "#444",
            letterSpacing: "0.03em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Tooltip title="Each AND group must match. Within a group, any one keyword (OR) is enough.">
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "#e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "help",
              fontSize: "0.65rem",
              fontWeight: 700,
              color: "#666",
            }}
          >
            ?
          </Box>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {groups.map((group, groupIdx) => (
          <React.Fragment key={groupIdx}>
            {/* AND connector between groups */}
            {groupIdx > 0 && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, pl: 1 }}
              >
                <Box
                  sx={{
                    width: 2,
                    height: 16,
                    background: "#bbb",
                    borderRadius: 1,
                    ml: 1,
                  }}
                />
                <Box
                  sx={{
                    px: 1,
                    py: 0.15,
                    borderRadius: "4px",
                    background: "#1a1a2e",
                    color: "#fff",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    userSelect: "none",
                  }}
                >
                  AND
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    height: 1,
                    background: "#e0e0e0",
                  }}
                />
              </Box>
            )}

            {/* Group card */}
            <Paper
              elevation={0}
              sx={{
                border: "1.5px solid",
                borderColor: group.length > 0 ? "#c5cae9" : "#e0e0e0",
                borderRadius: "8px",
                p: 1,
                background: group.length > 0 ? "#f8f9ff" : "#fafafa",
                transition: "border-color 0.2s, background 0.2s",
              }}
            >
              {/* Group header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: group.length > 0 ? 0.75 : 0,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: "4px",
                      background: "#3f51b5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.6rem",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    {groupIdx + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#888",
                      fontStyle: "italic",
                    }}
                  >
                    {group.length === 0
                      ? "Add keywords below — any one must match"
                      : group.length === 1
                        ? "Must match this keyword"
                        : "Any one of these must match (OR)"}
                  </Typography>
                </Box>
                {groups.length > 1 && (
                  <Tooltip title="Remove this AND group">
                    <IconButton
                      size="small"
                      onClick={() => removeGroup(groupIdx)}
                      disabled={disabled}
                      sx={{ p: 0.25, color: "#e57373" }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Keyword chips */}
              {group.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    mb: 0.75,
                  }}
                >
                  {group.map((kw, kwIdx) => (
                    <React.Fragment key={kwIdx}>
                      {kwIdx > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 0.5,
                            py: 0.15,
                            borderRadius: "4px",
                            background: "#e8f5e9",
                            color: "#2e7d32",
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            alignSelf: "center",
                          }}
                        >
                          OR
                        </Box>
                      )}
                      <Chip
                        label={kw}
                        size="small"
                        onDelete={
                          disabled
                            ? undefined
                            : () => removeKeyword(groupIdx, kwIdx)
                        }
                        sx={{
                          fontSize: "0.78rem",
                          height: 24,
                          background: "#e8eaf6",
                          color: "#283593",
                          fontWeight: 500,
                          border: "1px solid #c5cae9",
                          "& .MuiChip-deleteIcon": {
                            fontSize: 13,
                            color: "#9fa8da",
                            "&:hover": { color: "#e57373" },
                          },
                        }}
                      />
                    </React.Fragment>
                  ))}
                </Box>
              )}

              {/* Input row */}
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Type a keyword…"
                  value={inputs[groupIdx] ?? ""}
                  onChange={(e) => handleInputChange(groupIdx, e.target.value)}
                  onKeyDown={(e) => handleInputKeyDown(e, groupIdx)}
                  disabled={disabled}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-input": { fontSize: "0.8rem", py: "4px" },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      background: "#fff",
                    },
                  }}
                />
                <Tooltip
                  title={
                    group.length === 0
                      ? "Add keyword to this group"
                      : "Add as OR alternative in this group"
                  }
                >
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => addKeywordToGroup(groupIdx)}
                      disabled={disabled || !inputs[groupIdx]?.trim()}
                      sx={{
                        fontSize: "0.7rem",
                        py: 0.3,
                        px: 1,
                        minWidth: "unset",
                        whiteSpace: "nowrap",
                        borderColor: "#7986cb",
                        color: "#3f51b5",
                        "&:hover": {
                          borderColor: "#3f51b5",
                          background: "#e8eaf6",
                        },
                      }}
                    >
                      {group.length === 0 ? (
                        <>
                          <AddIcon sx={{ fontSize: 13, mr: 0.3 }} />
                          Add
                        </>
                      ) : (
                        <>
                          <AddIcon sx={{ fontSize: 13, mr: 0.3 }} />
                          OR
                        </>
                      )}
                    </Button>
                  </span>
                </Tooltip>
                {groupIdx === 0 && suggestAction}
              </Box>
            </Paper>
          </React.Fragment>
        ))}
      </Box>

      {/* Add AND group button */}
      {!hideAddRequiredGroup && (
        <Button
          size="small"
          variant="outlined"
          onClick={addGroup}
          disabled={disabled}
          startIcon={<AddIcon sx={{ fontSize: 13 }} />}
          sx={{
            mt: 1,
            fontSize: "0.72rem",
            py: 0.4,
            px: 1.2,
            borderColor: "#1a1a2e",
            color: "#1a1a2e",
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              background: "#1a1a2e",
              color: "#fff",
            },
          }}
        >
          + AND (add another required group)
        </Button>
      )}

      {/* Serialized preview + error */}
      <Box sx={{ mt: 0.75, display: "flex", alignItems: "center", gap: 1 }}>
        {/*value && (
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: "#aaa",
              fontFamily: "monospace",
              background: "#f5f5f5",
              px: 0.75,
              py: 0.2,
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            Saved as: <strong>{value}</strong>
          </Typography>
        )*/}
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ fontSize: "0.75rem" }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default KeywordBuilder;
